/* global G */
import { curry, getFirstItem } from 'lib/util'
import routeComposition from 'trait/composition/route'
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'

/**
 * With Key Event Handler
 *
 * Executes {@link redirect}, passing it either the current model's or sub-model's `key`
 * depending on `attribute`.
 *
 * @param {?string} attribute               string denoting the sub-model
 * @param {Gaia.AppModule.Spec} module      app module
 * @param {Gaia.Component.Spec} component   action component
 * @param {Gaia.PlatformEvent} event        event
 * @returns {Promise<void>}
 */
const withKey = async (attribute, module, component, event) => {
  event.preventDefault()
  event.stopPropagation()

  const model = module[G.MODEL]

  const key = attribute
    ? model[G.CHILDREN]?.[attribute]?.[G.STATE]?.[G.REF]
    : model[G.STATE]?.[G.REF]

  const moduleState = module[G.STATE]
  const { route } = component[G.CONFIGURATION]
  moduleState[G.ROUTE] = routeComposition(route.module, route.action)

  await redirectSequence(module)({ key })
}

/**
 * Executes {@link redirect}, passing it `module[G.MODEL][G.CHILDREN][attribute][G.STATE][G.REF]`.
 * This is useful for redirecting to an action in another module that needs the current
 * sub-model key.
 *
 * @type {(function(...[*]): (*))|*}
 */
export const withAttributeKey = curry(withKey)

/**
 * Executes {@link redirect}, passing it `module[G.MODEL][G.STATE][G.REF]`. This is useful
 * for redirecting to an action in another module that needs the current model key.
 */
export default curry((module, component, event) => withKey(null, module, component, event))
